import { ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";
import { formatCellValue } from "@/components/contracts/table-utils";
import { DataTable } from "@/components/shared/data-table";
import type { ContractDriver, ContractVehicle } from "@/lib/coach-manager/types";
import { cn } from "@/lib/utils";

function DriversTable({ drivers }: { drivers: ContractDriver[] }) {
  return (
    <DataTable
      headers={[{ label: "Driver ID" }, { label: "Driver Type" }, { label: "Default Driver ID" }]}
      className="border-none"
    >
      {drivers.map((driver) => (
        <tr key={driver.ContractDriverID} className="border-b last:border-b-0">
          <td className="px-4 py-2">{driver.ContractDriverID}</td>
          <td className="px-4 py-2">{formatCellValue(driver.DriverType)}</td>
          <td className="px-4 py-2">{formatCellValue(driver.DefaultDriverID)}</td>
        </tr>
      ))}
    </DataTable>
  );
}

/**
 * Vehicles table where each row expands to reveal that vehicle's own
 * `ContractDrivers[]` beneath it — the one nested expand/collapse kept in
 * this redesign, since Drivers are child data of one specific Vehicle, not
 * siblings under the Movement (unlike Pickups/Setdowns/etc).
 */
export function VehiclesTable({ vehicles }: { vehicles: ContractVehicle[] }) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (vehicles.length === 0) return null;

  return (
    <DataTable
      headers={[
        { label: "", className: "w-8" },
        { label: "Vehicle ID" },
        { label: "Vehicle Type" },
        { label: "Seats" },
        { label: "Revenue" },
        { label: "Default Vehicle ID" },
      ]}
    >
      {vehicles.map((vehicle) => {
        const isOpen = expandedId === vehicle.ContractVehicleID;
        const drivers = vehicle.ContractDrivers ?? [];
        return (
          <Fragment key={vehicle.ContractVehicleID}>
            <tr
              className={cn(
                "border-b last:border-b-0",
                drivers.length > 0 && "cursor-pointer hover:bg-muted/40",
              )}
              onClick={
                drivers.length > 0
                  ? () => setExpandedId(isOpen ? null : vehicle.ContractVehicleID)
                  : undefined
              }
            >
              <td className="px-2 py-2">
                {drivers.length > 0 && (
                  <ChevronRight
                    className={cn("h-4 w-4 transition-transform", isOpen && "rotate-90")}
                  />
                )}
              </td>
              <td className="px-4 py-2">{vehicle.ContractVehicleID}</td>
              <td className="px-4 py-2">{formatCellValue(vehicle.VehicleType)}</td>
              <td className="px-4 py-2">{vehicle.Seats}</td>
              <td className="px-4 py-2">{vehicle.Revenue}</td>
              <td className="px-4 py-2">{formatCellValue(vehicle.DefaultVehicleID)}</td>
            </tr>
            {isOpen && drivers.length > 0 && (
              <tr className="border-b bg-muted/20 last:border-b-0">
                <td colSpan={6} className="px-4 py-2">
                  <DriversTable drivers={drivers} />
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </DataTable>
  );
}
